/**
 * Client-side helpers for classifying prompt variables WITHOUT a
 * backend round-trip. Used in the new-prompt wizard and definition
 * editor so the operator sees "this is unknown — define it" as they
 * type.
 *
 * Stays in sync with the backend's PersonaState named fields and the
 * AUTO_HANDLED token list. If we add a new glossary field on the
 * backend, mirror it here.
 */

/** Glossary names that PersonaState resolves automatically per persona.
 *  Comparison is case + separator insensitive (normalise both sides). */
const GLOSSARY_NAMES: ReadonlySet<string> = new Set([
  'title',
  'firstname',
  'lastname',
  'duedate',
  'nextduedate',
  'statementdate',
  'monthend',
  'latestallowedptpdate',
  'overdue',
  'overdueamount',
  'installment',
  'installmentamount',
  'twoinstallments',
  'totaloutstanding',
  'agentname',
  'reasonnonpayment',
  'ptpdate',
  'ptpamount',
  'ptpsolution',
  'p1d1deferred',
  'p2d2deferred',
]);

const AUTO_HANDLED_PLAIN: ReadonlySet<string> = new Set(['today', 'timezone']);

/** Strip case, spaces, underscores, hyphens. Mirror of backend
 *  ``var_naming.normalise_var``. */
export function normaliseVar(name: string): string {
  let out = '';
  for (const c of name) {
    if (c === ' ' || c === '\t' || c === '_' || c === '-') continue;
    out += c.toLowerCase();
  }
  return out;
}

export type VariableClass = 'glossary' | 'auto_handled' | 'unknown';

export function classifyVariable(name: string): VariableClass {
  if (name.startsWith('dateutil:') || name.startsWith('date_range:')) {
    return 'auto_handled';
  }
  const norm = normaliseVar(name);
  if (AUTO_HANDLED_PLAIN.has(norm)) return 'auto_handled';
  if (GLOSSARY_NAMES.has(norm)) return 'glossary';
  return 'unknown';
}

/** Extract unique `{name}` placeholders from a prompt body in document
 *  order. Matches the backend's prompt_parser._TOKEN_RE. */
const _TOKEN_RE = /\{([A-Za-z][A-Za-z0-9_:\-+]*)\}/g;

export function extractVariables(body: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of body.matchAll(_TOKEN_RE)) {
    const name = m[1];
    const key = normaliseVar(name);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

export function extractUnknownVariables(body: string): string[] {
  return extractVariables(body).filter(
    (name) => classifyVariable(name) === 'unknown',
  );
}
